import { useEffect, useId, useRef, type ReactNode } from 'react';
import type React from 'react';
import { useFocusTrap } from '@shared-client/useFocusTrap.ts';

type EscapeMode = 'bubble' | 'capture' | 'none';

type ModalProps = {
  /**
   * Heading, and what the dialog is labelled by.
   *
   * Omit it for a dialog with its own bar (ManageCategoriesModal has a title,
   * a count and a close button in one row): Modal then renders no header at
   * all and `ariaLabel` does the labelling instead.
   */
  title?: ReactNode;
  /** Labels the dialog when there is no `title`. One or the other is required. */
  ariaLabel?: string;
  onClose: () => void;
  children?: ReactNode;
  /** Buttons. Rendered in `.modal__footer`; omit for a dialog with no verbs. */
  footer?: ReactNode;
  /** Extra class on the panel, e.g. `modal--confirm`. The variants are CSS. */
  className?: string;
  /** Extra class on the backdrop, e.g. `modal-backdrop--confirm` for z-index. */
  backdropClassName?: string;
  /** `modal__title--sm` for the small heading most dialogs use. */
  titleClassName?: string;
  /** Heading level. Defaults to h3, which is what the existing dialogs use. */
  titleAs?: 'h2' | 'h3';
  /**
   * How Escape is handled.
   *
   * - `bubble` (default) — a document listener, closes.
   * - `capture` — a CAPTURE-phase listener that also stops propagation. Set by
   *   `nested`; see below.
   * - `none` — the dialog refuses Escape, because something else has a prior
   *   claim on the key (a dirty form, an inline editor, a confirm on top).
   */
  escape?: EscapeMode;
  /**
   * This dialog was opened from INSIDE another one, so neither of the two ways
   * of dismissing it may reach its host.
   *
   * Escape becomes a CAPTURE-phase listener that stops propagation — capture
   * on `document` runs before every host's bubble-phase handler — and the
   * backdrop's mousedown stops propagating too, since the host's own backdrop
   * would otherwise close underneath. Without both, dismissing the picture
   * dismisses the picking room behind it, losing its search, its category, its
   * tab and every ticked asset.
   *
   * Do not "simplify" the capture phase away: `MediaPickerModal` has no
   * nested-dialog guard of its own and relies on this entirely.
   */
  nested?: boolean;
  /** Backdrop mousedown closes. Off for a dialog that must not be dismissed. */
  closeOnBackdrop?: boolean;
  /** Trap focus and lock the page behind. On unless a host owns focus itself. */
  trapFocus?: boolean;
  /**
   * Render the panel as a `<form>`. A dialog whose primary action is "submit
   * what I typed" wants Enter-in-a-field to fire it, and that is the form
   * element's job rather than a keydown handler per input.
   */
  panelAs?: 'div' | 'form';
  onSubmit?: (e: React.FormEvent) => void;
  /** Inline style on the PANEL, for values computed at render time — the deck
   *  overview sets its tile size as a custom property here. */
  style?: React.CSSProperties;
};

/**
 * A centred dialog: `Sheet`'s desktop sibling, and the same contract.
 *
 * There were twenty-six hand-rolled copies of this shell before it existed
 * (`grep 'className="modal-backdrop"'`), held together only by the CSS
 * convention in `styles/modal.css`. The variants had always been CSS
 * modifiers — `modal--confirm`, `--zoom`, `--add-slide`, `--new-deck`,
 * `--taxonomy` — so this is the React side catching up rather than a new
 * design.
 *
 * What the copies did not have, and what moving here is actually for:
 *
 * - **A focus trap.** `useFocusTrap` has existed the whole time and `Sheet`
 *   was its only caller, so the studio was more accessible on a phone than on
 *   a desktop: not one of the twenty-six kept Tab inside itself.
 * - **A scroll lock**, same story.
 * - **A unique label id.** `ConfirmModal` hard-coded
 *   `id="modal-confirm-title"`, so two confirms open at once emitted duplicate
 *   ids and a screen reader followed whichever came first.
 * - **One Escape**, spelled twelve different ways across the copies, three of
 *   them not at all. `SaveViewModal` and `DeckNameModal` had no document
 *   listener, so Escape worked only while their input had focus.
 *
 * The host still owns its trigger's `aria-expanded` / `aria-controls`: only
 * the host knows which button opened this.
 */
export default function Modal({
  title,
  ariaLabel,
  onClose,
  children,
  footer,
  className,
  backdropClassName,
  titleClassName = 'modal__title--sm',
  titleAs = 'h3',
  escape,
  nested = false,
  closeOnBackdrop = true,
  trapFocus = true,
  panelAs = 'div',
  onSubmit,
  style,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  useFocusTrap(panelRef, trapFocus);

  // onClose in the deps, not a ref, so a host that re-creates the callback
  // each render still closes exactly once.
  const escapeMode: EscapeMode = escape ?? (nested ? 'capture' : 'bubble');
  useEffect(() => {
    if (escapeMode === 'none') return;
    const capture = escapeMode === 'capture';
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (capture) e.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKey, capture);
    return () => document.removeEventListener('keydown', onKey, capture);
  }, [onClose, escapeMode]);

  // Lock the page behind the dialog. Restored to whatever it was rather than
  // to '', for the same reason Sheet does: a host stylesheet may set its own
  // overflow on body, and clearing it outright would leave that app
  // scrollable after every close.
  useEffect(() => {
    if (!trapFocus) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [trapFocus]);

  const Heading = titleAs;
  const Panel = panelAs;
  return (
    <div
      className={'modal-backdrop' + (backdropClassName ? ' ' + backdropClassName : '')}
      onMouseDown={
        closeOnBackdrop
          ? (e) => {
              if (nested) e.stopPropagation();
              onClose();
            }
          : undefined
      }
    >
      <Panel
        ref={panelRef as never}
        className={'modal' + (className ? ' ' + className : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? headingId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onSubmit={onSubmit}
        style={style}
      >
        {title && (
          <header className="modal__header">
            <Heading className={'modal__title' + (titleClassName ? ' ' + titleClassName : '')} id={headingId}>
              {title}
            </Heading>
          </header>
        )}
        {title ? <div className="modal__body">{children}</div> : children}
        {footer && <footer className="modal__footer">{footer}</footer>}
      </Panel>
    </div>
  );
}
